import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, PackagePlus, ExternalLink } from "lucide-react";

const Meta = ({ label, children }) => (
  <div><div className="text-xs tracking-wide text-muted-foreground">{label}</div><div className="mt-0.5 text-sm font-medium text-slate-900">{children}</div></div>
);

export default function StockReqHeader({ rfq, members, setOwner, onChanged }) {
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/catalog/stock/requisitions").then(({ data }) => setReq(data.find((r) => r.id === rfq.id) || { awarded: [] })).catch(() => setReq({ awarded: [] })), [rfq.id]);
  useEffect(() => { load(); }, [load, rfq.stage]);
  const create = async (sid) => {
    setBusy(sid);
    try {
      const { data } = await api.post("/catalog/stock/orders/from-requisition", { rfq_id: rfq.id, supplier_id: sid });
      toast.success("Stock PO created — attach anything else, then request it from Finance");
      onChanged?.(); nav(`/orders/som-po/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="mb-6 rounded-lg border border-border bg-white p-5" data-testid="stock-req-header">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="grid grid-cols-2 gap-x-10 gap-y-3 sm:grid-cols-4">
          <Meta label="Somcore Ref"><span className="font-mono">{rfq.code || "—"}</span></Meta>
          <Meta label="Type"><span className="inline-flex rounded-full bg-[#FBF6E9] px-2 py-0.5 text-xs font-semibold text-[#A87F31]" data-testid="rfq-stock-tag">Stock Requisition</span></Meta>
          <Meta label="Created"><span className="font-mono">{rfq.date_received || "—"}</span></Meta>
          <Meta label="Quotes Needed By"><span className="font-mono">{rfq.quote_deadline || "—"}</span></Meta>
          <div>
            <div className="mb-1 text-xs tracking-wide text-muted-foreground">Owner</div>
            <Select value={rfq.owner_id || ""} onValueChange={setOwner}>
              <SelectTrigger className="h-7 w-44 text-sm" data-testid="rfq-owner-field"><SelectValue placeholder="Assign owner">{rfq.owner_name || "Assign owner"}</SelectValue></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="min-w-[260px] flex-1 rounded-lg border border-dashed border-border bg-slate-50/60 p-3 sm:max-w-md" data-testid="stock-req-po-panel">
          <div className="mb-2 text-xs font-semibold text-slate-700">Stock PO</div>
          {req === null ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : !req.awarded.length ? (
            <p className="text-xs text-muted-foreground">Send the items to suppliers, capture their quotes, then award on Compare. Each awarded supplier gets a Create Stock PO button here.</p>
          ) : (
            <div className="space-y-1.5">
              {req.awarded.map((a) => (
                <div key={a.supplier_id} className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 text-xs" data-testid={`stock-req-sup-${a.supplier_id}`}>
                  <span><b className="text-slate-800">{a.supplier_name}</b> <span className="text-muted-foreground">· {a.lines} awarded</span></span>
                  {a.po_id ? (
                    <button onClick={() => nav(`/orders/som-po/${a.po_id}`)} className="inline-flex items-center gap-1 font-semibold text-[#1B4383] hover:underline" data-testid={`stock-req-open-po-${a.supplier_id}`}>Open Stock PO<ExternalLink className="h-3 w-3" /></button>
                  ) : (
                    <Button size="sm" onClick={() => create(a.supplier_id)} disabled={!!busy} className="h-7 bg-[#1B4383] px-2.5 text-xs hover:bg-[#16386e]" data-testid={`stock-req-create-po-${a.supplier_id}`}>{busy === a.supplier_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <PackagePlus className="mr-1 h-3 w-3" />}Create Stock PO</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
